import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  alpha,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  ClearAll as ClearAllIcon,
  CalendarMonth as CalendarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Optional: Import authentication context for enhanced UX
import { useAuth } from '../../../context/AuthContext';
import { ratingApi } from '../../../services/api';

// Types for the filters
const initialFilters = {
  organization_id: '',
  network_id: '',
  section_id: '',
  region: '',
  category_id: '',
  type_id: '',
  subtype_id: '',
  status: [],
  installDateFrom: null,
  installDateTo: null,
  rating_level_id: '', // New rating filter
  is_critical: '', // Critical asset filter
};

const AssetFilters = ({ 
  open, 
  onClose, 
  onApplyFilters, 
  currentFilters = initialFilters,
  assetsData = [],
  filterLookupData = {
    organizations: [], networks: [], sections: [],
    categories: [], types: [], subtypes: []
  },
  onRatingDataChange = () => {} // New callback to share rating data with parent
}) => {
  const theme = useTheme();
  
  // Optional: Get user context for enhanced UX
  const { user } = useAuth();
  
  const [filters, setFilters] = useState(currentFilters);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  
  // State for rating data
  const [ratingData, setRatingData] = useState({
    schemes: [],
    levels: [],
    loading: false,
    hasRatingScheme: false,
    error: null
  });

  // Use lookup data directly from props
  const {
    organizations = [],
    networks = [],
    sections = [],
    categories = [],
    types = [],
    subtypes = []
  } = filterLookupData;

  // Debug: Log data context (helpful for development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && open) {
      console.log('[AssetFilters] Data context:', {
        userRole: user?.role,
        userOrg: user?.organizationName,
        permissionScope: user?.permissionScope,
        totalAssets: assetsData.length,
        organizations: organizations.length,
        networks: networks.length,
        sections: sections.length
      });
    }
  }, [open, user, assetsData.length, organizations.length, networks.length, sections.length]);

  // Status options for checkboxes - matching asset_status_enum values
  const statusOptions = [
    { value: 'under_construction', label: 'Under Construction', color: '#ff7043' },
    { value: 'under_renovation', label: 'Under Renovation', color: '#9c27b0' },
    { value: 'active', label: 'Active', color: '#4caf50' },
    { value: 'maintenance', label: 'Maintenance', color: '#2196f3' },
    { value: 'inactive', label: 'Inactive', color: '#607d8b' },
    { value: 'decommissioned', label: 'Decommissioned', color: '#f44336' },
  ];

  // Region options derived from the regions actually present in the asset data
  // (rather than a hardcoded list), so they stay in sync with the database.
  // A preferred display order is applied first, then any unknown regions follow
  // alphabetically.
  const REGION_ORDER = ['Northern', 'Central', 'Southern', 'LPT2', 'Western', 'Eastern'];
  const regionOptions = Array.from(
    new Set(
      assetsData
        .map((asset) => asset.region_name)
        .filter((region) => region != null && region !== '')
    )
  )
    .sort((a, b) => {
      const ai = REGION_ORDER.indexOf(a);
      const bi = REGION_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map((region) => ({ value: region, label: region }));

  // Get filtered assets based on current location filters
  const getFilteredAssets = () => {
    return assetsData.filter(asset => {
      // Apply organization filter if set
      if (filters.organization_id && asset.organization_id !== filters.organization_id) {
        return false;
      }
      
      // Apply network filter if set
      if (filters.network_id && asset.network_id !== filters.network_id) {
        return false;
      }
      
      // Apply section filter if set
      if (filters.section_id && asset.section_id !== filters.section_id) {
        return false;
      }
      
      // Apply region filter if set
      if (filters.region && asset.region_name !== filters.region) {
        return false;
      }
      
      return true;
    });
  };

  // Calculate summary statistics for each filter
  const getOrganizationCount = (id) => 
    assetsData.filter(asset => asset.organization_id === id).length;
  
  const getNetworkCount = (id) => {
    const filteredByOrg = filters.organization_id 
      ? assetsData.filter(asset => asset.organization_id === filters.organization_id)
      : assetsData;
      
    return filteredByOrg.filter(asset => asset.network_id === id).length;
  };
  
  const getSectionCount = (id) => {
    let filteredAssets = assetsData;
    
    if (filters.organization_id) {
      filteredAssets = filteredAssets.filter(asset => 
        asset.organization_id === filters.organization_id
      );
    }
    
    if (filters.network_id) {
      filteredAssets = filteredAssets.filter(asset => 
        asset.network_id === filters.network_id
      );
    }
    
    return filteredAssets.filter(asset => asset.section_id === id).length;
  };
  
  const getRegionCount = (region) => {
    let filteredAssets = assetsData;
    
    if (filters.organization_id) {
      filteredAssets = filteredAssets.filter(asset => 
        asset.organization_id === filters.organization_id
      );
    }
    
    if (filters.network_id) {
      filteredAssets = filteredAssets.filter(asset => 
        asset.network_id === filters.network_id
      );
    }
    
    if (filters.section_id) {
      filteredAssets = filteredAssets.filter(asset => 
        asset.section_id === filters.section_id
      );
    }
    
    return filteredAssets.filter(asset => 
      asset.region_name === region
    ).length;
  };
  
  const getCategoryCount = (id) => {
    // Get assets already filtered by location filters
    const locationFilteredAssets = getFilteredAssets();
    
    // Count assets with a direct category match
    const directMatches = locationFilteredAssets.filter(asset => 
      asset.category_id === id
    ).length;
    
    // Get the category name
    const catName = categories.find(cat => cat.id === id)?.name;
    
    // Count by name match
    const nameMatches = catName ? 
      locationFilteredAssets.filter(asset => asset.category_name === catName).length : 0;
    
    // Count by type's category
    // Use types directly from props (already contains all types)
    const relevantTypeIds = types
      .filter(type => type.category_id === id)
      .map(type => type.id);
    
    const typeMatches = locationFilteredAssets.filter(asset => 
      relevantTypeIds.includes(asset.asset_type_id)
    ).length;
    
    // Return the maximum count (to avoid double-counting)
    return Math.max(directMatches, nameMatches, typeMatches);
  };
  
  const getTypeCount = (id) => {
    // Get assets filtered by current location filters and category if selected
    let filteredAssets = getFilteredAssets();
    
    // Additionally filter by category if it's selected
    if (filters.category_id) {
      // Get the category name to help with matching
      const categoryName = categories.find(cat => cat.id === filters.category_id)?.name;
      
      filteredAssets = filteredAssets.filter(asset => {
        const directCategoryMatch = asset.category_id === filters.category_id;
        const categoryNameMatch = categoryName && asset.category_name === categoryName;
        
        // Get all types associated with this category
        // Use types directly from props
        const allTypesInCategory = types
          .filter(t => t.category_id === filters.category_id)
          .map(t => t.id);
        
        const typeCategoryMatch = allTypesInCategory.includes(asset.asset_type_id);
        
        return directCategoryMatch || categoryNameMatch || typeCategoryMatch;
      });
    }
    
    // Count assets with this type - Fix: use asset_type_id instead of type_id
    return filteredAssets.filter(asset => asset.asset_type_id === id).length;
  };
  
  const getSubtypeCount = (id) => {
    // Get assets filtered by current location filters, category, and type
    let filteredAssets = getFilteredAssets();
    
    // Additionally filter by category and type if selected
    if (filters.type_id) {
      filteredAssets = filteredAssets.filter(asset => asset.asset_type_id === filters.type_id);
    } else if (filters.category_id) {
      // Get all types in this category
      // Use types directly from props
      const typesInCategory = types
        .filter(t => t.category_id === filters.category_id)
        .map(t => t.id);
      
      filteredAssets = filteredAssets.filter(asset => 
        typesInCategory.includes(asset.asset_type_id)
      );
    }
    
    // Count assets with this subtype
    return filteredAssets.filter(asset => asset.subtype_id === id).length;
  };
  
  const getStatusCount = (status) => {
    const locationFilteredAssets = getFilteredAssets();
    return locationFilteredAssets.filter(asset => 
      asset.status?.toLowerCase() === status.toLowerCase()
    ).length;
  };

  // Get count of assets for each rating level
  const getRatingLevelCount = (ratingLevelId) => {
    // Get assets filtered by current location + classification filters
    let filteredAssets = getFilteredAssets();
    
    // Apply classification filters
    if (filters.category_id) {
      const categoryName = categories.find(cat => cat.id === filters.category_id)?.name;
      
      filteredAssets = filteredAssets.filter(asset => {
        const directCategoryMatch = asset.category_id === filters.category_id;
        const categoryNameMatch = categoryName && asset.category_name === categoryName;
        
        // Get all types associated with this category
        const allTypesInCategory = types
          .filter(t => t.category_id === filters.category_id)
          .map(t => t.id);
        
        const typeCategoryMatch = allTypesInCategory.includes(asset.asset_type_id);
        
        return directCategoryMatch || categoryNameMatch || typeCategoryMatch;
      });
    }
    
    // Apply type filter if selected
    if (filters.type_id) {
      filteredAssets = filteredAssets.filter(asset => 
        asset.asset_type_id === filters.type_id
      );
    }
    
    // Count assets with this rating level
    if (ratingLevelId === 'null' || ratingLevelId === null) {
      return filteredAssets.filter(asset => 
        !asset.rating_level_id || asset.rating_level_id === null
      ).length;
    }
    
    return filteredAssets.filter(asset => 
      asset.rating_level_id === ratingLevelId
    ).length;
  };

  // Update filters state if the external currentFilters prop changes
  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  // Fetch rating data when asset type changes
  useEffect(() => {
    if (filters.type_id && user?.organizationId) {
      fetchRatingDataForAssetType(filters.type_id);
    } else {
      // Clear rating data when no type selected
      setRatingData({ 
        schemes: [], 
        levels: [], 
        loading: false, 
        hasRatingScheme: false,
        error: null 
      });
      setFilters(prev => ({ ...prev, rating_level_id: '' }));
      
      // Clear rating data in parent component
      onRatingDataChange([]);
    }
  }, [filters.type_id, user?.organizationId]);

  // Function to fetch rating data for selected asset type
  const fetchRatingDataForAssetType = async (assetTypeId) => {
    setRatingData(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      console.log(`[AssetFilters] Fetching rating data for asset type ${assetTypeId}, org ${user.organizationId}`);
      
      // Fetch rating scheme for this organization + asset type
      const schemeResponse = await ratingApi.getSchemes({
        organization_id: user.organizationId,
        asset_type_id: assetTypeId
      });
      
      console.log('[AssetFilters] Full scheme response:', schemeResponse);
      console.log('[AssetFilters] Scheme data:', schemeResponse.data);
      
      // Handle nested data structure: response.data.data
      const schemesData = schemeResponse.data?.data || schemeResponse.data;
      console.log('[AssetFilters] Actual schemes array:', schemesData);
      console.log('[AssetFilters] Data length:', schemesData?.length);
      console.log('[AssetFilters] Condition check:', schemesData && schemesData.length > 0);
      
      if (schemesData && schemesData.length > 0) {
        const scheme = schemesData[0]; // Should be only one per org+type
        console.log(`[AssetFilters] Found rating scheme: ${scheme.name} (ID: ${scheme.id})`);
        
        // Fetch rating levels for this scheme
        const levelsResponse = await ratingApi.getLevels({
          scheme_id: scheme.id
        });
        
        // Handle nested data structure for levels too
        const levelsData = levelsResponse.data?.data || levelsResponse.data;
        console.log(`[AssetFilters] Found ${levelsData?.length || 0} rating levels`);
        
        setRatingData({
          schemes: schemesData,
          levels: levelsData || [],
          loading: false,
          hasRatingScheme: true,
          error: null
        });
        
        // Share rating data with parent component
        onRatingDataChange(levelsData || []);
      } else {
        console.log(`[AssetFilters] No rating scheme found for asset type ${assetTypeId}`);
        setRatingData({
          schemes: [],
          levels: [],
          loading: false,
          hasRatingScheme: false,
          error: null
        });
        
        // Clear rating data in parent component
        onRatingDataChange([]);
      }
    } catch (error) {
      console.error('[AssetFilters] Error fetching rating data:', error);
      setRatingData({
        schemes: [],
        levels: [],
        loading: false,
        hasRatingScheme: false,
        error: error.message || 'Failed to load rating data'
      });
      
      // Clear rating data in parent component on error
      onRatingDataChange([]);
    }
  };

  // Auto-select organization for non-admin users
  useEffect(() => {
    if (user && user.role !== 'system_admin' && user.organizationId && organizations.length > 0) {
      // Find the user's organization in the list
      const userOrg = organizations.find(org => org.id === user.organizationId);
      if (userOrg && !filters.organization_id) {
        // Auto-select the user's organization
        setFilters(prev => ({
          ...prev,
          organization_id: user.organizationId
        }));
      }
    }
  }, [user, organizations, filters.organization_id]);

  // Count active filters for the badge
  useEffect(() => {
    let count = 0;
    if (filters.organization_id) count++;
    if (filters.network_id) count++;
    if (filters.section_id) count++;
    if (filters.region) count++;
    if (filters.category_id) count++;
    if (filters.type_id) count++;
    if (filters.subtype_id) count++;
    if (filters.status && filters.status.length > 0) count++;
    if (filters.installDateFrom) count++;
    if (filters.installDateTo) count++;
    if (filters.rating_level_id) count++; // Add rating to count
    if (filters.is_critical !== '') count++; // Add critical to count
    setActiveFilterCount(count);
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    
    // Reset dependent fields when parent field changes
    if (name === 'organization_id') {
      setFilters({
        ...filters,
        [name]: value,
        network_id: '',
        section_id: '',
      });
    } else if (name === 'network_id') {
      setFilters({
        ...filters,
        [name]: value,
        section_id: '',
      });
    } else if (name === 'category_id') {
      setFilters({
        ...filters,
        [name]: value,
        type_id: '',
        subtype_id: '',
        rating_level_id: '', // Reset rating when category changes
      });
    } else if (name === 'type_id') {
      setFilters({
        ...filters,
        [name]: value,
        subtype_id: '',
        rating_level_id: '', // Reset rating when type changes
      });
    } else {
      setFilters({
        ...filters,
        [name]: value,
      });
    }
  };

  // Handle status checkbox changes
  const handleStatusChange = (status) => {
    const currentStatus = [...filters.status];
    const statusIndex = currentStatus.indexOf(status);
    
    if (statusIndex === -1) {
      // Add the status if it's not already in the array
      currentStatus.push(status);
    } else {
      // Remove the status if it's already in the array
      currentStatus.splice(statusIndex, 1);
    }
    
    setFilters({
      ...filters,
      status: currentStatus,
    });
  };

  // Handle date changes
  const handleDateChange = (name, date) => {
    setFilters({
      ...filters,
      [name]: date,
    });
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  // Apply filters
  const handleApplyFilters = () => {
    // Pass the selected filter IDs directly back to the parent
    onApplyFilters(filters);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Filter Assets
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* User context indicator */}
        {user && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
              {user.role === 'system_admin' ? (
                `System Admin View • All Organizations`
              ) : user.organizationName ? (
                `${user.organizationName}${user.permissionScope === 'network_section' ? ' • Limited Access' : ''}`
              ) : (
                'Organization-scoped access'
              )}
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              Filtering {assetsData.length} available assets
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} applied
          </Typography>
          <Button
            startIcon={<ClearAllIcon />}
            size="small"
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0}
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {/* Scrollable content area */}
      <Box 
        sx={{ 
          flex: 1, 
          overflowY: 'auto',
          px: 3,
          pb: 2,
          // Add padding bottom to ensure content doesn't get hidden behind buttons
          paddingBottom: '80px' 
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
            Location
          </Typography>
          
          {user?.permissionScope === 'network_section' && (
            <Box sx={{ mb: 2, p: 1, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
              <Typography variant="caption" color="info.main" sx={{ fontWeight: 500 }}>
                ℹ️ Limited Access: You can only filter within your assigned networks and sections
              </Typography>
            </Box>
          )}
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="organization-label">Organization</InputLabel>
            <Select
              labelId="organization-label"
              id="organization_id"
              name="organization_id"
              value={filters.organization_id}
              label="Organization"
              onChange={handleFilterChange}
              disabled={user?.role !== 'system_admin'}
            >
              <MenuItem value="">
                <em>All Organizations</em>
              </MenuItem>
              {organizations?.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name} ({getOrganizationCount(org.id)})
                  {user?.role !== 'system_admin' && org.id === user?.organizationId && (
                    <span style={{ marginLeft: 8, fontSize: '0.8em', opacity: 0.7 }}>• Your org</span>
                  )}
                </MenuItem>
              ))}
              {user?.role !== 'system_admin' && organizations.length === 0 && (
                <MenuItem disabled>
                  <em>Your organization only</em>
                </MenuItem>
              )}
            </Select>
            {user?.role !== 'system_admin' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                🔒 Organization locked to your access scope
              </Typography>
            )}
            {user?.role === 'system_admin' && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, ml: 1 }}>
                ⭐ System Admin: You can select any organization
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!filters.organization_id}>
            <InputLabel id="network-label">Network</InputLabel>
            <Select
              labelId="network-label"
              id="network_id"
              name="network_id"
              value={filters.network_id}
              label="Network"
              onChange={handleFilterChange}
              disabled={!filters.organization_id}
            >
              <MenuItem value="">
                <em>All Networks</em>
              </MenuItem>
              {/* Render networks directly as they are pre-filtered by parent component */}
              {networks?.map((network) => (
                <MenuItem key={network.id} value={network.id}>
                  {network.name} ({getNetworkCount(network.id)})
                </MenuItem>
              ))}
            </Select>
            {!filters.organization_id && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                Select an organization first
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!filters.network_id}>
            <InputLabel id="section-label">Section</InputLabel>
            <Select
              labelId="section-label"
              id="section_id"
              name="section_id"
              value={filters.section_id}
              label="Section"
              onChange={handleFilterChange}
              disabled={!filters.network_id}
            >
              <MenuItem value="">
                <em>All Sections</em>
              </MenuItem>
              {/* Filter sections based on selected network */}
              {sections?.filter(sec => !filters.network_id || sec.network_id === filters.network_id).map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.name} ({getSectionCount(section.id)})
                </MenuItem>
              ))}
            </Select>
            {!filters.network_id && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                Select a network first
              </Typography>
            )}
          </FormControl>
          
          {/* Region Filter */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="region-label">Region</InputLabel>
            <Select
              labelId="region-label"
              id="region"
              name="region"
              value={filters.region}
              label="Region"
              onChange={handleFilterChange}
            >
              <MenuItem value="">
                <em>All Regions</em>
              </MenuItem>
              {regionOptions
                .map((region) => ({ ...region, count: getRegionCount(region.value) }))
                // Only show regions that have assets; keep the currently
                // selected region visible even if its count drops to 0.
                .filter((region) => region.count > 0 || region.value === filters.region)
                .map((region) => (
                  <MenuItem key={region.value} value={region.value}>
                    {region.label} ({region.count})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
            Classification
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              id="category_id"
              name="category_id"
              value={filters.category_id}
              label="Category"
              onChange={handleFilterChange}
            >
              <MenuItem value="">
                <em>All Categories</em>
              </MenuItem>
              {categories?.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name} ({getCategoryCount(category.id)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!filters.category_id}>
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              id="type_id"
              name="type_id"
              value={filters.type_id}
              label="Type"
              onChange={handleFilterChange}
              disabled={!filters.category_id} // Disable if no category selected
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {/* Filter types based on selected category */}
              {types?.filter(t => !filters.category_id || t.category_id === filters.category_id)
                    .map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name} ({getTypeCount(type.id)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }} disabled={!filters.type_id}>
            <InputLabel id="subtype-label">Subtype</InputLabel>
            <Select
              labelId="subtype-label"
              id="subtype_id"
              name="subtype_id"
              value={filters.subtype_id}
              label="Subtype"
              onChange={handleFilterChange}
              disabled={!filters.type_id} // Disable if no type selected
            >
              <MenuItem value="">
                <em>All Subtypes</em>
              </MenuItem>
              {/* Filter subtypes based on selected type */}
              {subtypes?.filter(st => !filters.type_id || st.type_id === filters.type_id)
                        .map((subtype) => (
                <MenuItem key={subtype.id} value={subtype.id}>
                  {subtype.name} ({getSubtypeCount(subtype.id)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Rating Section - appears only when asset type is selected and has rating scheme */}
        {ratingData.hasRatingScheme && (
          <>
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Rating
              </Typography>
              
              {/* Show scheme name as context */}
              {ratingData.schemes[0] && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Scheme: {ratingData.schemes[0].name}
                  {ratingData.schemes[0].description && ` - ${ratingData.schemes[0].description}`}
                </Typography>
              )}
              
              {/* Show error if rating data failed to load */}
              {ratingData.error && (
                <Box sx={{ mb: 2, p: 1, backgroundColor: alpha(theme.palette.error.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
                  <Typography variant="caption" color="error.main" sx={{ fontWeight: 500 }}>
                    ⚠️ Error loading rating data: {ratingData.error}
                  </Typography>
                </Box>
              )}
              
              <FormControl fullWidth sx={{ mb: 2 }} disabled={ratingData.loading}>
                <InputLabel id="rating-label">Rating Level</InputLabel>
                <Select
                  labelId="rating-label"
                  id="rating_level_id"
                  name="rating_level_id"
                  value={filters.rating_level_id}
                  label="Rating Level"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">
                    <em>All Rating Levels</em>
                  </MenuItem>
                  
                  {/* Not Rated option */}
                  <MenuItem value="null">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: '#9E9E9E',
                            mr: 1
                          }}
                        />
                        <Typography>Not Rated</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        ({getRatingLevelCount(null)})
                      </Typography>
                    </Box>
                  </MenuItem>
                  
                  {/* Rating levels from scheme */}
                  {ratingData.levels
                    .sort((a, b) => (b.value || 0) - (a.value || 0)) // Sort by value descending
                    .map((level) => (
                    <MenuItem key={level.id} value={level.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: level.color_code || '#666',
                              mr: 1
                            }}
                          />
                          <Typography>{level.name}</Typography>
                          {level.value !== null && level.value !== undefined && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              (Value: {level.value})
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          ({getRatingLevelCount(level.id)})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                
                {ratingData.loading && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                    🔄 Loading rating levels...
                  </Typography>
                )}
                
                {!ratingData.loading && ratingData.levels.length === 0 && ratingData.hasRatingScheme && !ratingData.error && (
                  <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, ml: 1 }}>
                    ⚠️ No rating levels configured for this asset type
                  </Typography>
                )}
              </FormControl>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
            Status
          </Typography>
          
          <FormGroup>
            {statusOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={filters.status.includes(option.value)}
                    onChange={() => handleStatusChange(option.value)}
                    sx={{
                      color: option.color,
                      '&.Mui-checked': {
                        color: option.color,
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({getStatusCount(option.value)})
                    </Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Critical Asset Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
            Critical Assets
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="critical-label">Critical Status</InputLabel>
            <Select
              labelId="critical-label"
              id="is_critical"
              name="is_critical"
              value={filters.is_critical}
              label="Critical Status"
              onChange={handleFilterChange}
            >
              <MenuItem value="">
                <em>All Assets</em>
              </MenuItem>
              <MenuItem value="true">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    size="small"
                    label="CRITICAL"
                    sx={{
                      color: '#fff',
                      backgroundColor: '#d32f2f',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                    }}
                  />
                  <Typography>Critical Only</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="false">
                <Typography>Non-Critical Only</Typography>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
            Installation Date
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label="From"
                  value={filters.installDateFrom}
                  onChange={(date) => handleDateChange('installDateFrom', date)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                  inputFormat="MM/dd/yyyy"
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="To"
                  value={filters.installDateTo}
                  onChange={(date) => handleDateChange('installDateTo', date)}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                  inputFormat="MM/dd/yyyy"
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      </Box>

      {/* Sticky buttons at the bottom */}
      <Box 
        sx={{ 
          p: 3,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex', 
          justifyContent: 'space-between',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
          boxShadow: '0px -4px 8px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApplyFilters}
          startIcon={<FilterIcon />}
          disabled={activeFilterCount === 0}
        >
          Apply Filters
        </Button>
      </Box>
    </Drawer>
  );
};

export default AssetFilters; 